import { useQuery, useQueryClient } from '@tanstack/react-query';

import ApiSite from '@Api/Site';
import useSearchParams from '@Hooks/useSearchParams';
import generateQueryKeys from '@Queries/helpers/generateQueryKeys';
import { useRequest } from '@Contexts/Request';
import { useDevPanel } from '@Stores/DevPanel';
import { useAppConfig } from '@Contexts/AppConfig';
import useNavigation from '@Navigation/useNavigation';

import type { UseQueryResult } from '@tanstack/react-query';
import type { BasePage } from '@Types/Base';

function usePage<T>(): UseQueryResult<T & BasePage> {
  const request = useRequest();
  const { region, secondaryRegion } = request;
  const queryClient = useQueryClient();
  const location = useNavigation();

  const { currentDate: devCurrentDate } = useDevPanel();
  const currentDate = devCurrentDate || new Date();
  const { isProduction } = useAppConfig();
  const searchParams = useSearchParams();

  if (!isProduction) {
    searchParams.set('currentDate', (+currentDate).toString());
  }

  const url = `${location.pathname}?${searchParams}`;
  const keys = generateQueryKeys({
    keys: ['page', region, secondaryRegion],
    pathname: location.pathname,
    search: location.search,
    request,
  });
  const placeholder: BasePage = {
    meta: {
      tags: [],
    },
    page: '',
  };

  const query = useQuery({
    queryKey: keys,
    queryFn: () => {
      const cache = queryClient.getQueryData<T & BasePage>(keys);
      if (cache) return Promise.resolve(cache);

      return ApiSite.fetchPage<T & BasePage>({ url, request });
    },
    enabled: true,
    placeholderData: (prev) => prev || (placeholder as any),
  });

  return query;
}

export default usePage;
